import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { UserIcon } from '@/components/icons';
import { useI18n } from '@/i18n/I18nProvider';
import { formatEmiratesId } from '@/lib/formatters';
import type { PartnershipCharterResponse } from '@/lib/hooks/useGetCharter';

const PLACEHOLDER = '-';

function InfoField({
  label,
  value,
  span = 1,
  mono = false,
  isRequired = false,
  isMissing = false,
}: {
  label: string;
  value?: React.ReactNode;
  span?: 1 | 2;
  mono?: boolean;
  isRequired?: boolean;
  isMissing?: boolean;
}) {
  return (
    <div className={`space-y-2 ${span === 2 ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center gap-2">
        <div className="block text-sm font-medium text-foreground mb-1">
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </div>
        {isMissing && (
          <span className="text-xs text-destructive bg-destructive/10 px-2 py-0.5 rounded">مطلوب / Required</span>
        )}
      </div>
      <div
        className={`bg-muted rounded px-3 py-2 text-sm ${mono ? 'font-mono' : ''} ${isMissing ? 'border-2 border-destructive' : ''}`}
      >
        {value ?? PLACEHOLDER}
      </div>
    </div>
  );
}

interface ParentInfoCardProps {
  charterRecord?: PartnershipCharterResponse | null;
}

export function ParentInfoCard({ charterRecord }: ParentInfoCardProps) {
  const { t } = useI18n();

  const parentFullName = charterRecord?.parentName || PLACEHOLDER;
  const parentEid = charterRecord?.parentEmiratesId || PLACEHOLDER;
  const primaryPhone = charterRecord?.studentPhone || PLACEHOLDER;
  const studentEmail = charterRecord?.studentEmail || PLACEHOLDER;
  const studentFullName = charterRecord?.studentName || PLACEHOLDER;
  const studentNationalId = charterRecord?.studentEmiratesId || PLACEHOLDER;
  const studentAddress = charterRecord?.studentAddress || PLACEHOLDER;
  const hasValidStudentAddress = charterRecord?.hasValidStudentAddress ?? true;

  return (
    <Card className="border shadow-sm">
      <CardHeader className="rounded-t-xl bg-primary text-primary-foreground">
        <CardTitle className="text-lg flex items-center">
          <UserIcon className="me-2" />
          {t.parentConduct.parentSection.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-primary-foreground/80">
              {t.parentConduct.parentSection.parentData}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoField label={t.parentConduct.parentSection.parentName} value={parentFullName} />
              <InfoField label={t.parentConduct.parentSection.parentNationalId} value={formatEmiratesId(parentEid)} mono />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="block text-sm font-medium text-foreground mb-1">
                    {t.parentConduct.parentSection.contactNumber}
                    <span className="text-destructive ml-1">*</span>
                  </div>
                </div>
                <div className="bg-muted rounded px-3 py-2 text-sm">{primaryPhone || PLACEHOLDER}</div>
              </div>
              <InfoField
                label={t.parentConduct.parentSection.parentEmail}
                value={studentEmail || PLACEHOLDER}
              />
            </div>
          </section>

          <Separator />

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-primary-foreground/80">
              {t.parentConduct.parentSection.linkedStudentData}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoField label={t.parentConduct.parentSection.studentFullName} value={studentFullName} />
              <InfoField label={t.parentConduct.schoolSection.nationalId} value={formatEmiratesId(studentNationalId)} mono />
              <InfoField
                label={t.parentConduct.parentSection.parentAddress}
                value={studentAddress}
                span={2}
                isRequired={true}
                isMissing={!hasValidStudentAddress}
              />
            </div>
          </section>
        </div>
      </CardContent>
    </Card>
  );
}
